import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Plus, Edit3, Trash2, Search, Tag, Eye, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// iter85 — Pricing Type / Customer Tier. Must mirror backend ALLOWED_PRICING_TYPES.
const PRICING_TYPES = [
  { value: 'residential',                label: 'Residential' },
  { value: 'commercial',                 label: 'Commercial' },
  { value: 'property_management',        label: 'Property Management' },
  { value: 'warranty_company',           label: 'Warranty Company' },
  { value: 'internal_nexflow_warranty',  label: 'Internal / NexFlow Warranty' },
];

const labelOfPricingType = (v) => PRICING_TYPES.find((p) => p.value === v)?.label || v;

const emptyForm = {
  sku: '',
  name: '',
  description: '',
  price: 0,
  labor_minutes: 0,
  tags: '',
  department: '',
  pricing_type: 'residential',
  warranty_company_id: '',
  active: true,
};

const Pricebook = () => {
  const { user } = useAuth();
  const canEdit = ['super_admin', 'admin'].includes(user?.role);
  const [items, setItems] = useState([]);
  const [warrantyCompanies, setWarrantyCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({
    department: '',
    pricing_type: '',
    warranty_company_id: '',
    active: '',
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (query = '', f = filters) => {
    setLoading(true);
    try {
      const params = {};
      if (query) params.q = query;
      if (f.department) params.department = f.department;
      if (f.pricing_type) params.pricing_type = f.pricing_type;
      if (f.warranty_company_id) params.warranty_company_id = f.warranty_company_id;
      if (f.active !== '') params.active = f.active === 'true';
      const { data } = await axios.get(`${API}/admin/pricebook`, { withCredentials: true, params });
      setItems(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load pricebook');
    }
    setLoading(false);
  }, [filters]);

  const loadWarrantyCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/warranty-companies`, { withCredentials: true });
      setWarrantyCompanies(data || []);
    } catch (e) {
      // silent fail — list is optional for non-warranty items
    }
  }, []);

  useEffect(() => {
    loadWarrantyCompanies();
    load('', filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(q, filters);
  };

  const applyFilter = (key, value) => {
    const next = { ...filters, [key]: value === '__all__' ? '' : value };
    if (key === 'pricing_type' && value !== 'warranty_company') {
      // Reset warranty company filter when pricing type changes away from warranty_company
      next.warranty_company_id = '';
    }
    setFilters(next);
    load(q, next);
  };

  const clearFilters = () => {
    const next = { department: '', pricing_type: '', warranty_company_id: '', active: '' };
    setFilters(next);
    setQ('');
    load('', next);
  };

  const activeWarrantyCompanies = useMemo(
    () => warrantyCompanies.filter((w) => w.active !== false),
    [warrantyCompanies],
  );

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      sku: it.sku || '',
      name: it.name || '',
      description: it.description || '',
      price: it.price || 0,
      labor_minutes: it.labor_minutes || 0,
      tags: (it.tags || []).join(', '),
      department: it.department || '',
      pricing_type: it.pricing_type || 'residential',
      warranty_company_id: it.warranty_company_id || '',
      active: it.active !== false,
    });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.pricing_type === 'warranty_company' && !form.warranty_company_id) {
      toast.error('Pick a Warranty Company (HSP, etc.) for warranty-tier pricing.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        sku: form.sku,
        name: form.name,
        description: form.description,
        price: parseFloat(form.price || 0),
        labor_minutes: parseInt(form.labor_minutes || 0, 10),
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        department: form.department || '',
        pricing_type: form.pricing_type || 'residential',
        warranty_company_id: form.pricing_type === 'warranty_company' ? (form.warranty_company_id || null) : null,
        active: !!form.active,
      };
      if (editing) {
        // iter86 — patch the row in place so the table doesn't flash to "Loading…"
        // (which collapses height and resets scroll position). User stays exactly where they were.
        await axios.put(`${API}/admin/pricebook/${editing.id}`, body, { withCredentials: true });
        setItems((prev) => prev.map((it) => it.id === editing.id ? { ...it, ...body, id: editing.id } : it));
        toast.success('Item updated');
      } else {
        const { data } = await axios.post(`${API}/admin/pricebook`, body, { withCredentials: true });
        setItems((prev) => [{ ...data }, ...prev]);
        toast.success('Item created');
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete pricebook item "${it.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/pricebook/${it.id}`, { withCredentials: true });
      // iter86 — patch in place; don't reload (preserves scroll position).
      setItems((prev) => prev.filter((x) => x.id !== it.id));
      toast.success('Item deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const wcNameOf = (id) => warrantyCompanies.find((w) => w.id === id)?.name || '—';
  const hasAnyFilter = Object.values(filters).some((v) => v !== '');

  return (
    <div data-testid="pricebook" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <form onSubmit={onSearch} className="flex gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#525252]" />
            <Input data-testid="pricebook-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search SKU, name, tag…" className="pl-8 bg-[#0A0A0A] border-[#262626] text-white w-56 sm:w-72" />
          </div>
          <Button type="submit" variant="outline" className="border-[#262626] text-white hover:bg-[#262626]">Search</Button>
        </form>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          {canEdit && (
            <DialogTrigger asChild>
              <Button data-testid="add-pricebook-item" onClick={openCreate} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-2" /> New Item</Button>
            </DialogTrigger>
          )}
          {!canEdit && (
            <Badge className="bg-[#262626] text-[#A3A3A3] border-transparent flex items-center gap-1"><Eye className="w-3 h-3" /> View only</Badge>
          )}
          <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-lg max-h-[90vh] overflow-y-auto" style={{ scrollbarGutter: 'stable' }}>
            <DialogHeader><DialogTitle>{editing ? 'Edit Pricebook Item' : 'New Pricebook Item'}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white">SKU</Label>
                  <Input data-testid="pb-sku" required value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })} className="bg-[#0A0A0A] border-[#262626] text-white font-mono" />
                </div>
                <div>
                  <Label className="text-white">Department</Label>
                  <Select value={form.department || 'all'} onValueChange={(v) => setForm({ ...form, department: v === 'all' ? '' : v })}>
                    <SelectTrigger data-testid="pb-department" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      <SelectItem value="all" className="text-white">All Departments</SelectItem>
                      <SelectItem value="service" className="text-white">Service</SelectItem>
                      <SelectItem value="new_construction" className="text-white">New Construction</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-white">Name</Label>
                <Input data-testid="pb-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div>
                <Label className="text-white">Description</Label>
                <Textarea data-testid="pb-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white">Price ($)</Label>
                  <Input data-testid="pb-price" required type="number" min={0} step={0.01} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
                <div>
                  <Label className="text-white">Labor (min)</Label>
                  <Input data-testid="pb-labor" type="number" min={0} value={form.labor_minutes} onChange={(e) => setForm({ ...form, labor_minutes: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
              </div>

              {/* iter85 — Pricing Type & conditional Warranty Company */}
              <div>
                <Label className="text-white">Pricing Type / Customer Tier</Label>
                <Select value={form.pricing_type} onValueChange={(v) => setForm({ ...form, pricing_type: v, warranty_company_id: v === 'warranty_company' ? form.warranty_company_id : '' })}>
                  <SelectTrigger data-testid="pb-pricing-type" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    {PRICING_TYPES.map((p) => (
                      <SelectItem key={p.value} value={p.value} className="text-white" data-testid={`pb-pricing-type-${p.value}`}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.pricing_type === 'internal_nexflow_warranty' && (
                  <p className="text-[10px] text-[#A3A3A3] mt-1.5 flex items-start gap-1.5">
                    <ShieldAlert className="w-3 h-3 mt-0.5 text-[#D4AF37] shrink-0" />
                    Used for callback / re-work tracking only. No customer or warranty invoice is generated.
                  </p>
                )}
              </div>

              {form.pricing_type === 'warranty_company' && (
                <div data-testid="pb-warranty-fields" className="space-y-2 bg-[#0A0A0A] border border-[#D4AF37]/40 rounded-md p-3">
                  <Label className="text-white text-xs uppercase tracking-wider text-[#D4AF37] font-bold">Warranty Company</Label>
                  <Select value={form.warranty_company_id || ''} onValueChange={(v) => setForm({ ...form, warranty_company_id: v })}>
                    <SelectTrigger data-testid="pb-warranty-company" className="bg-[#0A0A0A] border-[#262626] text-white">
                      <SelectValue placeholder="Select warranty company…" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      {activeWarrantyCompanies.length === 0 && (
                        <div className="px-2 py-3 text-xs text-[#A3A3A3]">No warranty companies yet.</div>
                      )}
                      {activeWarrantyCompanies.map((w) => (
                        <SelectItem key={w.id} value={w.id} className="text-white" data-testid={`pb-wc-${w.id}`}>{w.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {canEdit && (
                    <Link to="/admin/settings?tab=warranty_companies" data-testid="pb-add-warranty-company-link" className="text-[#D4AF37] text-[11px] hover:text-[#F3E5AB] inline-flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Add / manage warranty companies
                    </Link>
                  )}
                </div>
              )}

              <div>
                <Label className="text-white">Tags <span className="text-[#525252] text-xs">(comma-separated)</span></Label>
                <Input data-testid="pb-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="install, emergency" className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="pb-active" />
                <span className="text-white text-sm">Active (appears in pricing dropdowns)</span>
              </div>
              <Button data-testid="pb-save" type="submit" disabled={saving} className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{saving ? 'Saving…' : (editing ? 'Update Item' : 'Create Item')}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* iter85 — Filter row */}
      <div data-testid="pricebook-filters" className="bg-[#141414] border border-[#262626] rounded-md p-3 flex flex-wrap items-center gap-3">
        <span className="text-[10px] uppercase tracking-wider text-[#525252]">Filters</span>
        <Select value={filters.department || '__all__'} onValueChange={(v) => applyFilter('department', v)}>
          <SelectTrigger data-testid="pb-filter-department" className="bg-[#0A0A0A] border-[#262626] text-white h-9 w-44 text-xs"><SelectValue placeholder="Department" /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-[#262626]">
            <SelectItem value="__all__" className="text-white text-xs">All Departments</SelectItem>
            <SelectItem value="service" className="text-white text-xs">Service</SelectItem>
            <SelectItem value="new_construction" className="text-white text-xs">New Construction</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.pricing_type || '__all__'} onValueChange={(v) => applyFilter('pricing_type', v)}>
          <SelectTrigger data-testid="pb-filter-pricing-type" className="bg-[#0A0A0A] border-[#262626] text-white h-9 w-52 text-xs"><SelectValue placeholder="Pricing Type" /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-[#262626]">
            <SelectItem value="__all__" className="text-white text-xs">All Pricing Types</SelectItem>
            {PRICING_TYPES.map((p) => (
              <SelectItem key={p.value} value={p.value} className="text-white text-xs">{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filters.pricing_type === 'warranty_company' && (
          <Select value={filters.warranty_company_id || '__all__'} onValueChange={(v) => applyFilter('warranty_company_id', v)}>
            <SelectTrigger data-testid="pb-filter-warranty-company" className="bg-[#0A0A0A] border-[#262626] text-white h-9 w-48 text-xs"><SelectValue placeholder="Warranty Company" /></SelectTrigger>
            <SelectContent className="bg-[#141414] border-[#262626]">
              <SelectItem value="__all__" className="text-white text-xs">All Companies</SelectItem>
              {warrantyCompanies.map((w) => (
                <SelectItem key={w.id} value={w.id} className="text-white text-xs">{w.name}{w.active === false ? ' (inactive)' : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={filters.active === '' ? '__all__' : filters.active} onValueChange={(v) => applyFilter('active', v)}>
          <SelectTrigger data-testid="pb-filter-active" className="bg-[#0A0A0A] border-[#262626] text-white h-9 w-36 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-[#262626]">
            <SelectItem value="__all__" className="text-white text-xs">All Status</SelectItem>
            <SelectItem value="true" className="text-white text-xs">Active only</SelectItem>
            <SelectItem value="false" className="text-white text-xs">Inactive only</SelectItem>
          </SelectContent>
        </Select>
        {hasAnyFilter && (
          <button type="button" data-testid="pb-clear-filters" onClick={clearFilters} className="text-[#D4AF37] text-xs hover:text-[#F3E5AB] underline">
            Clear filters
          </button>
        )}
      </div>

      <div className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto">
        {loading ? (
          <div className="p-6 text-[#A3A3A3]">Loading…</div>
        ) : items.length === 0 ? (
          <div data-testid="pricebook-empty" className="p-8 text-center text-[#A3A3A3]">
            {hasAnyFilter || q
              ? <>No items match the current filters. <button onClick={clearFilters} className="text-[#D4AF37] underline hover:text-[#F3E5AB]">Clear filters</button></>
              : 'No pricebook items yet. Click "New Item" to add your first price.'}
          </div>
        ) : (
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-[#0A0A0A] text-[#A3A3A3] text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">SKU</th>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Dept</th>
                <th className="text-left px-4 py-2">Pricing Tier</th>
                <th className="text-right px-4 py-2">Price</th>
                <th className="text-right px-4 py-2">Labor</th>
                <th className="text-left px-4 py-2">Tags</th>
                {canEdit && <th className="text-right px-4 py-2">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} data-testid={`pb-row-${it.id}`} className="border-t border-[#262626] hover:bg-[#1a1a1a]">
                  <td className="px-4 py-2 text-white font-mono text-xs">{it.sku}</td>
                  <td className="px-4 py-2 text-white">
                    <div className="flex items-center gap-2">
                      {it.name}
                      {!it.active && <Badge className="bg-[#262626] text-[#A3A3A3] border-transparent">inactive</Badge>}
                    </div>
                    {it.description && <p className="text-[#525252] text-xs">{it.description}</p>}
                  </td>
                  <td className="px-4 py-2 text-[#A3A3A3] capitalize">{it.department ? it.department.replace('_', ' ') : '—'}</td>
                  <td className="px-4 py-2 text-[#A3A3A3]">
                    <div className="text-xs">{labelOfPricingType(it.pricing_type || 'residential')}</div>
                    {it.pricing_type === 'warranty_company' && (
                      <div className="text-[10px] text-[#D4AF37]">{wcNameOf(it.warranty_company_id)}</div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right text-[#D4AF37] font-medium">${(it.price || 0).toFixed(2)}</td>
                  <td className="px-4 py-2 text-right text-[#A3A3A3]">{it.labor_minutes || 0}m</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(it.tags || []).map((t) => (
                        <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-[#262626] text-[#A3A3A3] flex items-center gap-1"><Tag className="w-2.5 h-2.5" /> {t}</span>
                      ))}
                    </div>
                  </td>
                  {canEdit && (
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <Button data-testid={`pb-edit-${it.id}`} size="sm" variant="outline" onClick={() => openEdit(it)} className="border-[#262626] text-white hover:bg-[#262626]"><Edit3 className="w-3.5 h-3.5" /></Button>
                        <Button data-testid={`pb-delete-${it.id}`} size="sm" variant="outline" onClick={() => remove(it)} className="border-red-700 text-red-400 hover:bg-red-900/30"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Pricebook;
